import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { ComputeResourceDiscoveryApi } from './ComputeResourceDiscoveryApi';
import {
  ResourceDetailsType,
  ListComputeResourceResponse,
  ExecuteActionAllOptions,
  ExecuteActionResponse,
  ExecuteActionOptions,
  CRDResourceRequestBody,
  CRDActionRequestBody,
  CRDActionAllRequestBody,
  ListComputeResourceOptions,
  GetKubernetesNamespacesResponse,
} from './types';

/**
 * Client for interacting with the Compute Resource Discovery API.
 *
 * @implements {ComputeResourceDiscoveryApi}
 */
export interface ComputeResourceDiscoveryClientOptions {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  cacheDuration?: number; // in milliseconds
}

export class ComputeResourceDiscoveryClient
  implements ComputeResourceDiscoveryApi
{
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly cache = new Map<
    string,
    { timestamp: number; response: any }
  >();
  private readonly CACHE_DURATION: number;

  public constructor(options: ComputeResourceDiscoveryClientOptions) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.CACHE_DURATION = options.cacheDuration ?? 5 * 60 * 1000; // Default 5 minutes
  }

  private generateCacheKey(prefix: string, data: any): string {
    // Create a stable cache key from the data
    const sortedData = JSON.stringify(data, Object.keys(data).sort());
    return `${prefix}:${sortedData}`;
  }

  private async fetchWithCache<T>(
    key: string,
    fetcher: () => Promise<T>,
  ): Promise<T> {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.response as T;
    }
    const response = await fetcher();
    this.cache.set(key, { timestamp: Date.now(), response });
    return response;
  }

  public invalidateCache(pattern?: string): void {
    if (!pattern) {
      // Clear all cache
      this.cache.clear();
      return;
    }

    // Invalidate cache entries matching the pattern
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  public getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }

  public async listResources(
    options: ListComputeResourceOptions,
  ): Promise<ResourceDetailsType[]> {
    const urlSegment = `resources`;
    const cacheKey = this.generateCacheKey('listResources', options);

    return this.fetchWithCache(cacheKey, async () => {
      const response = await this.post<ListComputeResourceResponse>(
        urlSegment,
        {
          serviceType: options.serviceType,
          resourceType: options.resourceType,
          entityRef: options.entityRef,
          cloudConfig: options.cloudConfig,
          projectCode: options.projectCode,
        } as CRDResourceRequestBody,
      );

      return response.response ?? [];
    });
  }

  public async executeAction(
    options: ExecuteActionOptions,
  ): Promise<ExecuteActionResponse> {
    switch (options.serviceType.toLowerCase()) {
      case 'aws': {
        if (
          !options.aws ||
          !options.aws.region ||
          !options.aws.accountId ||
          !options.aws.projectCode
        ) {
          throw new Error(
            'Missing required AWS options: region, accountId, and projectCode',
          );
        }
        const urlSegment = `action`;

        const response = await this.post<ExecuteActionResponse>(urlSegment, {
          serviceType: options.serviceType,
          entityRef: options.entityRef,
          details: options.aws,
        } as CRDActionRequestBody);
        this.invalidateCache();
        return response;
      }
      case 'kubernetes': {
        if (
          !options.kubernetes ||
          !options.kubernetes.kubeContext ||
          !options.kubernetes.projectCode
        ) {
          throw new Error(
            'Missing required Kubernetes options: kubeContext and projectCode',
          );
        }
        const urlSegment = `action`;

        const response = await this.post<ExecuteActionResponse>(urlSegment, {
          serviceType: options.serviceType,
          entityRef: options.entityRef,
          details: options.kubernetes,
        } as CRDActionRequestBody);
        this.invalidateCache();
        return response;
      }
      case 'gcp': {
        if (
          !options.gcp ||
          !options.gcp.projectId ||
          !options.gcp.projectCode
        ) {
          throw new Error(
            'Missing required GCP options: projectId and projectCode',
          );
        }
        const urlSegment = `action`;

        const response = await this.post<ExecuteActionResponse>(urlSegment, {
          serviceType: options.serviceType,
          entityRef: options.entityRef,
          details: options.gcp,
        } as CRDActionRequestBody);
        this.invalidateCache();
        return response;
      }
      case 'azure': {
        if (
          !options.azure ||
          !options.azure.resourceGroup ||
          !options.azure.projectCode
        ) {
          throw new Error(
            'Missing required Azure options: resourceGroup and projectCode',
          );
        }
        const urlSegment = `action`;

        const response = await this.post<ExecuteActionResponse>(urlSegment, {
          serviceType: options.serviceType,
          entityRef: options.entityRef,
          details: options.azure,
        } as CRDActionRequestBody);
        this.invalidateCache();
        return response;
      }
      default:
        throw new Error(`Unsupported service: ${options.serviceType}`);
    }
  }

  public async executeAllAction(
    options: ExecuteActionAllOptions,
  ): Promise<ExecuteActionResponse> {
    const urlSegment = `action/all`;

    const response = await this.post<ExecuteActionResponse>(urlSegment, {
      entityRef: options.entityRef,
      aws: options.aws,
      kubernetes: options.kubernetes,
      gcp: options.gcp,
      azure: options.azure,
    } as CRDActionAllRequestBody);

    this.invalidateCache();
    return response;
  }

  public async getKubernetesNamespaceAnnotations(
    options: ListComputeResourceOptions,
  ): Promise<GetKubernetesNamespacesResponse> {
    const urlSegment = `resources/kubernetes-namespace`;
    const cacheKey = this.generateCacheKey(
      'getKubernetesNamespaceAnnotations',
      options,
    );

    return this.fetchWithCache(cacheKey, () =>
      this.post<GetKubernetesNamespacesResponse>(urlSegment, {
        serviceType: options.serviceType,
        resourceType: options.resourceType,
        entityRef: options.entityRef,
        cloudConfig: options.cloudConfig,
        projectCode: options.projectCode,
      } as CRDResourceRequestBody),
    );
  }

  private async post<T>(path: string, body: any): Promise<T> {
    const baseUrl = `${await this.discoveryApi.getBaseUrl(
      'compute-resource-discovery',
    )}/`;
    const url = new URL(path, baseUrl);

    const response = await this.fetchApi.fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error((errorData.error as Error).message);
    }

    return response.json() as Promise<T>;
  }
}
